/* eslint-disable @next/next/no-img-element */
"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
  CustomLeftArrow,
  CustomRightArrow
} from "@/components/shared/CarouselMulti/carouselMulti";

import type { ButtonGroupType, GalleryProps } from "./gallery.types";

const CarouselMulti = dynamic(
  () => import("@/components/shared/CarouselMulti/carouselMulti"),
  {
    loading: () => (
      <Skeleton width="100%" height={284} className="gallery-image" />
    ),
    ssr: false
  }
);

import "./styles.scss";

const TABS = {
  photos: "fotos",
  videos: "videos"
};

const ButtonGroup = ({
  next,
  previous,
  carouselState,
  variantButton
}: ButtonGroupType) => {
  const currentSlide = carouselState?.currentSlide || 0;
  const counter = `${currentSlide + 1} / ${carouselState?.totalItems}`;

  return (
    <>
      <div className="button-group">
        <CustomLeftArrow
          onClick={() => !!previous && previous()}
          carouselState={carouselState}
          outlined={false}
          variantButton={variantButton}
        />
        <span>{counter}</span>
        <CustomRightArrow
          onClick={() => !!next && next()}
          carouselState={carouselState}
          outlined={false}
          variantButton={variantButton}
        />
      </div>
      <div className="counter-container">
        <span className="button-group-counter">{counter}</span>
      </div>
    </>
  );
};

const Gallery = ({
  images,
  label,
  buttonOutline = true,
  variantButton,
  variantTabs = "tabs-lemon",
  variantDots = "lemon"
}: GalleryProps) => {
  const [tabSelected, setTabSelected] = useState<"fotos" | "videos">("fotos");
  const [initialSlide, setInitialSlide] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showImagesModal, setShowImagesModal] = useState(false);

  const handleShowModal = (index?: number) => {
    if (index) {
      setInitialSlide(index);
    }
    setShowModal(true);
    setTimeout(() => {
      setShowImagesModal(true);
    }, 500);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowImagesModal(false);
  };

  useEffect(() => {
    if (showModal) {
      window.document.body.style.overflow = "hidden";
    } else {
      window.document.body.style.overflow = "auto";
      setInitialSlide(0);
    }
  }, [showModal]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModal]);

  return (
    <>
      <section className="wrapper">
        <div className="gallery-mobile">
          <CarouselMulti
            showDots
            slidesMobile={1}
            slidesTablet={1}
            variantDots={variantDots}
          >
            {images.map((image, index) => (
              <img
                key={index}
                src={image.url}
                alt={image.name}
                className="gallery-image"
                style={{
                  cursor: "pointer"
                }}
                onClick={() => {
                  handleShowModal(index);
                }}
              />
            ))}
          </CarouselMulti>
        </div>
        <div className="gallery-desktop">
          <div className="gallery-desktop-container">
            {images.slice(0, 5).map((image, index) => (
              <div key={index} className="gallery-image-container">
                <img
                  key={index}
                  src={image.url}
                  alt={image.name}
                  className="gallery-image"
                  style={{
                    cursor: "pointer"
                  }}
                  onClick={() => {
                    handleShowModal(index);
                  }}
                />
              </div>
            ))}
          </div>
          <button
            className={`btn-modal-carousel btn ${buttonOutline ? "outlined" : ""} ${variantButton}`}
            onClick={() => handleShowModal()}
          >
            Mostrar todas as fotos
          </button>
        </div>
      </section>
      {showModal && (
        <div className="overlay dark">
          <div className="modal">
            <div className="modal-header">
              <div className="heading-xs">{label}</div>
              <FontAwesomeIcon
                className="link-icon-left close-button"
                icon={faXmark}
                fontSize={32}
                style={{
                  color: "var(--token-colors-neutral-lnk-light)",
                  cursor: "pointer",
                  padding: "4px 8px"
                }}
                onClick={() => handleCloseModal()}
              />
            </div>
            <div className="tabs-container tabs-horizontal">
              <div className={`tabs ${variantTabs} medium`} role="tablist">
                <button
                  className={`tab tab-top-icon ${tabSelected === TABS.photos && "active"}`}
                  role="tab"
                  aria-selected={tabSelected === TABS.photos}
                  aria-controls="fotos-tab"
                  id="fotos"
                  onClick={() => setTabSelected("fotos")}
                >
                  Fotos
                </button>
                {/* <button
                className={`tab tab-top-icon ${tabSelected === TABS.videos && "active"}`}
                role="tab"
                aria-selected={tabSelected === TABS.videos}
                aria-controls="videos-tab"
                id="videos"
                onClick={() => setTabSelected("videos")}
              >
                Vídeos
              </button> */}
              </div>
            </div>
            <div className="modal-content">
              <CarouselMulti
                slidesMobile={1}
                slidesTablet={1}
                slidesDesk={1}
                customButtonGroup={
                  <ButtonGroup variantButton={variantButton} />
                }
                initialSlide={initialSlide}
              >
                {images.map((image, index) => (
                  <img
                    key={index}
                    src={image.url}
                    alt={image.name}
                    style={{
                      opacity: showImagesModal ? 1 : 0,
                      transition: "opacity 0.3s"
                    }}
                  />
                ))}
              </CarouselMulti>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Gallery;
